/**
 * WholesaleEngine — /v3/intel/wholesale-engine
 *
 * V3-native surface for BP-002 (Wholesale). Standalone page (property-
 * agnostic) that lets the operator pick any property from the workspace,
 * plug in the key wholesale inputs, and instantly see:
 *   • Buyer MAO / Seller MAO / Recommended Offer / Assignment Fee
 *   • Buyer ROI, Seller Net Proceeds, ARV coverage
 *   • Deal Score ring + classification chip + warnings
 *   • Recent saved scenarios (across the workspace)
 *
 * All math lives in the backend engine — this file is a thin, premium
 * presentation layer over `/api/v3/properties/{id}/wholesale/calculate`
 * and `/api/v3/properties/{id}/wholesale/scenarios`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Calculator, ChevronDown, Loader2, Save, RefreshCw,
  ArrowUpRight, AlertTriangle, Info, ShieldAlert, Sparkles,
  DollarSign, Percent, TrendingUp,
} from "lucide-react";
import DailyBusinessScore from "@/pages/v3/DailyBusinessScore";
import useSelectedProperty from "@/hooks/useSelectedProperty";

// ─────────────────────────────────────────── constants ──────────────────

const DEFAULT_INPUTS = {
  arv: "",
  arvSource: "manual",
  arvConfidence: "0.70",
  askingPrice: "",
  repairCost: "",
  repairSource: "manual",
  buyerRulePercent: "0.70",
  targetAssignmentFee: "15000",
  buyerClosingCosts: "5000",
  buyerHoldingCosts: "0",
  buyerFinancingCosts: "0",
  buyerResaleCosts: "0",
  sellerClosingCosts: "3000",
  sellerPayoffs: "0",
  sellerArrears: "0",
  offerCushion: "0",
};

const CLASS_META = {
  excellent: { label: "EXCELLENT",  color: "#059669" },
  strong:    { label: "STRONG",     color: "#10B981" },
  review:    { label: "GOOD",       color: "#F59E0B" },
  weak:      { label: "WEAK",       color: "#EA580C" },
  pass:      { label: "PASS",       color: "#DC2626" },
};

// ─────────────────────────────────────────── helpers ────────────────────

function fmtMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

function fmtMoneyFull(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `$${Math.round(v).toLocaleString()}`;
}

function fmtPct(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function timeAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

// ─────────────────────────────────────────── page ──────────────────────

export default function WholesaleEngine() {
  const [properties, setProperties] = useState([]);
  const { propertyId, setPropertyId } = useSelectedProperty(properties);
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [scenarioName, setScenarioName] = useState("Wholesale — Base Case");
  const [result, setResult] = useState(null); // last calculated
  const [saved, setSaved] = useState(null);   // last saved scenario doc
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [propsLoading, setPropsLoading] = useState(true);

  const propertyMap = useMemo(() => {
    const map = {};
    (properties || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [properties]);

  // Load workspace properties for the selector.
  useEffect(() => {
    let alive = true;
    setPropsLoading(true);
    api.get("/v3/properties?limit=200").then(({ data }) => {
      if (!alive) return;
      const list = data?.items || [];
      setProperties(list);
    }).catch(() => toast.error("Couldn't load properties"))
      .finally(() => { if (alive) setPropsLoading(false); });
    return () => { alive = false; };
  }, []);

  // When the selected property changes, seed inputs from its own values
  // (ARV, repairs, asking price) so the operator gets a hot start.
  useEffect(() => {
    if (!propertyId || !propertyMap[propertyId]) return;
    const p = propertyMap[propertyId];
    setInputs((prev) => ({
      ...prev,
      arv: p.estimated_arv ? String(p.estimated_arv) : prev.arv,
      repairCost: p.estimated_repairs ? String(p.estimated_repairs) : prev.repairCost,
      askingPrice: p.asking_price ? String(p.asking_price) : prev.askingPrice,
    }));
    setResult(null);
    setSaved(null);
  }, [propertyId, propertyMap]);

  // Load recent saved scenarios for the selected property.
  const loadRecent = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const { data } = await api.get(`/v3/properties/${pid}/wholesale/scenarios`);
      setRecent(data?.items || []);
    } catch { setRecent([]); }
  }, []);
  useEffect(() => { loadRecent(propertyId); }, [propertyId, loadRecent]);

  const setInput = (k) => (e) => setInputs((f) => ({ ...f, [k]: e.target.value }));

  const submit = useCallback(async (save = false) => {
    if (!propertyId) {
      toast.error("Pick a property first.");
      return;
    }
    if (!inputs.arv || parseFloat(inputs.arv) <= 0) {
      toast.error("Enter an ARV greater than $0.");
      return;
    }
    const setter = save ? setSaving : setCalculating;
    setter(true);
    try {
      const payload = {
        scenarioName,
        save,
        inputs: {
          ...inputs,
          arv: inputs.arv,
          arvConfidence: inputs.arvConfidence || "0.5",
          askingPrice: inputs.askingPrice || null,
          repairCost: inputs.repairCost || "0",
        },
      };
      const { data } = await api.post(
        `/v3/properties/${propertyId}/wholesale/calculate`,
        payload,
      );
      setResult(data);
      if (save) {
        setSaved(data);
        toast.success("Scenario saved");
        loadRecent(propertyId);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail
          : Array.isArray(detail) ? detail[0]?.msg || "Calculation failed"
          : "Calculation failed",
      );
    } finally {
      setter(false);
    }
  }, [inputs, scenarioName, propertyId, loadRecent]);

  const outputs = result?.outputs || null;
  const score = outputs?.dealScore ?? 0;
  const classification = outputs?.classification || null;
  const classMeta = classification ? CLASS_META[classification] : null;
  const warnings = result?.warnings || [];
  const scoreFactors = result?.score_factors || {};

  return (
    <div className="space-y-6" data-testid="wholesale-engine-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
            Intelligence Engine
          </div>
          <h1 className="mt-1 text-[32px] font-bold text-slate-900 tracking-tight leading-tight">
            Wholesale Engine
          </h1>
          <p className="mt-2 text-slate-600 text-[14px] max-w-2xl">
            Instant MAO, assignment fee optimizer, and Deal Score — running against
            your workspace&apos;s live pricing model.
          </p>
        </div>
        <PropertyPicker
          properties={properties}
          value={propertyId}
          onChange={setPropertyId}
          loading={propsLoading}
        />
      </div>

      {/* Empty state — no properties in workspace */}
      {!propsLoading && properties.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-14 text-center shadow-sm"
             data-testid="wholesale-empty-no-props">
          <Calculator className="w-8 h-8 mx-auto mb-3 text-amber-500" />
          <div className="text-slate-900 font-bold text-lg">No properties yet</div>
          <div className="text-sm text-slate-500 mt-1 mb-5">
            Add a property first — the Wholesale Engine analyses individual deals.
          </div>
          <Link to="/v3/crm/pipeline" className="inline-flex items-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-3.5 py-2 shadow-md hover:shadow-lg"
                style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            Open Pipeline <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {propertyId && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-4">
          {/* ─── INPUTS PANEL ─── */}
          <section
            data-testid="wholesale-inputs-panel"
            className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
          >
            <div className="h-1 w-full" style={{ backgroundColor: "#F59E0B" }} />
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg grid place-items-center text-white shadow-md"
                   style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
                <Calculator className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">Inputs</div>
                <div className="text-[15px] font-bold text-slate-900 leading-tight">
                  Scenario builder
                </div>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <FormGroup label="Scenario name">
                <input
                  type="text" value={scenarioName}
                  onChange={(e) => setScenarioName(e.target.value)}
                  data-testid="wholesale-input-scenarioName"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                />
              </FormGroup>

              <div className="grid grid-cols-2 gap-3">
                <MoneyInput label="ARV" value={inputs.arv} onChange={setInput("arv")}
                            testid="wholesale-input-arv" required />
                <FormGroup label="ARV Confidence">
                  <select value={inputs.arvConfidence}
                          onChange={setInput("arvConfidence")}
                          data-testid="wholesale-input-arvConfidence"
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                    <option value="0.90">High (0.90)</option>
                    <option value="0.70">Medium (0.70)</option>
                    <option value="0.50">Low (0.50)</option>
                    <option value="0.30">Very Low (0.30)</option>
                  </select>
                </FormGroup>
                <MoneyInput label="Repair Cost" value={inputs.repairCost}
                            onChange={setInput("repairCost")} testid="wholesale-input-repairCost" />
                <MoneyInput label="Asking Price" value={inputs.askingPrice}
                            onChange={setInput("askingPrice")} testid="wholesale-input-askingPrice" />
                <PctInput label="Buyer Rule %" value={inputs.buyerRulePercent}
                          onChange={setInput("buyerRulePercent")}
                          testid="wholesale-input-buyerRulePercent" required />
                <MoneyInput label="Target Fee" value={inputs.targetAssignmentFee}
                            onChange={setInput("targetAssignmentFee")}
                            testid="wholesale-input-targetAssignmentFee" />
              </div>

              <details className="rounded-lg border border-slate-200 bg-slate-50/40 px-3 py-2"
                       data-testid="wholesale-adv-toggle">
                <summary className="cursor-pointer text-[11px] uppercase tracking-widest font-bold text-slate-500 select-none">
                  Advanced (holding · closing · payoffs)
                </summary>
                <div className="grid grid-cols-2 gap-3 pt-3">
                  <MoneyInput label="Buyer Closing" value={inputs.buyerClosingCosts}
                              onChange={setInput("buyerClosingCosts")} />
                  <MoneyInput label="Buyer Holding" value={inputs.buyerHoldingCosts}
                              onChange={setInput("buyerHoldingCosts")} />
                  <MoneyInput label="Seller Closing" value={inputs.sellerClosingCosts}
                              onChange={setInput("sellerClosingCosts")} />
                  <MoneyInput label="Seller Payoffs" value={inputs.sellerPayoffs}
                              onChange={setInput("sellerPayoffs")} />
                  <MoneyInput label="Seller Arrears" value={inputs.sellerArrears}
                              onChange={setInput("sellerArrears")} />
                  <MoneyInput label="Offer Cushion" value={inputs.offerCushion}
                              onChange={setInput("offerCushion")} />
                </div>
              </details>

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => submit(false)}
                  disabled={calculating || saving}
                  data-testid="wholesale-calculate-btn"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-semibold px-4 py-2 shadow-sm disabled:opacity-60 transition-colors"
                >
                  {calculating ? <Loader2 className="w-4 h-4 animate-spin" />
                               : <RefreshCw className="w-4 h-4" />}
                  Calculate
                </button>
                <button
                  type="button"
                  onClick={() => submit(true)}
                  disabled={saving || calculating || !result}
                  data-testid="wholesale-save-btn"
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-4 py-2 shadow-md disabled:opacity-60 transition-all"
                  style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Save className="w-4 h-4" />}
                  Save
                </button>
              </div>
            </div>
          </section>

          {/* ─── OUTPUTS PANEL ─── */}
          <section
            data-testid="wholesale-outputs-panel"
            className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
          >
            <div className="h-1 w-full"
                 style={{ backgroundColor: classMeta?.color || "#334155" }} />
            {!outputs ? (
              <div className="p-10 grid place-items-center text-center min-h-[400px]">
                <div>
                  <div className="mx-auto h-16 w-16 rounded-2xl grid place-items-center bg-amber-50 border border-amber-200 mb-4">
                    <Sparkles className="w-7 h-7 text-amber-600" />
                  </div>
                  <div className="text-slate-900 font-bold text-lg">Ready to analyze</div>
                  <div className="text-sm text-slate-500 mt-1 max-w-xs">
                    Fill in ARV + Rule %, then hit <b>Calculate</b> to see
                    Buyer MAO, Recommended Offer, and the Deal Score.
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-5 space-y-5" data-testid="wholesale-results">
                {/* Score row */}
                <div className="flex items-center gap-5">
                  <DailyBusinessScore
                    score={score}
                    size="sm"
                    label="Deal score"
                    testid="wholesale-score-ring"
                  />
                  <div className="min-w-0 flex-1">
                    {classMeta && (
                      <div
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ring-1"
                        data-testid="wholesale-classification-chip"
                        style={{
                          backgroundColor: `${classMeta.color}15`,
                          color: classMeta.color,
                          borderColor: `${classMeta.color}40`,
                        }}
                      >
                        <span className="h-1.5 w-1.5 rounded-full"
                              style={{ backgroundColor: classMeta.color }} />
                        {classMeta.label} DEAL
                      </div>
                    )}
                    <div className="mt-1.5 text-[13px] text-slate-600 leading-snug">
                      For this property, our Recommended Offer to the seller is{" "}
                      <b className="text-slate-900" data-testid="wholesale-out-recommendedOffer">
                        {fmtMoneyFull(outputs.recommendedOffer)}
                      </b>{" "}
                      with an assignment fee of{" "}
                      <b className="text-slate-900" data-testid="wholesale-out-actualAssignmentFee">
                        {fmtMoneyFull(outputs.actualAssignmentFee)}
                      </b>.
                    </div>
                  </div>
                </div>

                {/* KPI grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <KpiTile
                    label="Buyer MAO" color="#3B82F6" icon={DollarSign}
                    value={fmtMoney(outputs.buyerMAO)}
                    testid="wholesale-kpi-buyerMAO"
                  />
                  <KpiTile
                    label="Seller MAO" color="#F59E0B" icon={DollarSign}
                    value={fmtMoney(outputs.sellerMAO)}
                    testid="wholesale-kpi-sellerMAO"
                  />
                  <KpiTile
                    label="Assignment Fee" color="#8B5CF6" icon={DollarSign}
                    value={fmtMoney(outputs.actualAssignmentFee)}
                    testid="wholesale-kpi-fee"
                  />
                  <KpiTile
                    label="Buyer Equity" color="#10B981" icon={TrendingUp}
                    value={fmtMoney(outputs.buyerEquity)}
                    testid="wholesale-kpi-buyerEquity"
                  />
                  <KpiTile
                    label="Buyer ROI" color="#0EA5E9" icon={Percent}
                    value={fmtPct(outputs.buyerROI)}
                    testid="wholesale-kpi-buyerROI"
                  />
                  <KpiTile
                    label="Seller Net" color="#EA580C" icon={DollarSign}
                    value={fmtMoney(outputs.sellerNetProceeds)}
                    testid="wholesale-kpi-sellerNet"
                  />
                  <KpiTile
                    label="Repair / ARV" color="#94A3B8" icon={Percent}
                    value={fmtPct(outputs.repairToARV)}
                    testid="wholesale-kpi-repairToARV"
                  />
                  <KpiTile
                    label="Offer / ARV" color="#334155" icon={Percent}
                    value={fmtPct(outputs.offerToARV)}
                    testid="wholesale-kpi-offerToARV"
                  />
                </div>

                {/* Warnings */}
                {warnings.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5 space-y-1.5"
                       data-testid="wholesale-warnings">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3" /> Warnings
                    </div>
                    {warnings.map((w, i) => (
                      <div key={i} className="text-[12px] text-slate-700 flex items-start gap-1.5"
                           data-testid={`wholesale-warning-${w.code}`}>
                        <WarningIcon severity={w.severity} />
                        <div className="flex-1">{w.message}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Score factors */}
                {Object.keys(scoreFactors).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
                      Score breakdown
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                      {Object.entries(scoreFactors).map(([k, v]) => (
                        <div key={k} className="rounded-lg bg-slate-50/70 border border-slate-100 px-2.5 py-1.5"
                             data-testid={`wholesale-factor-${k}`}>
                          <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 truncate">
                            {k.replaceAll("_", " ")}
                          </div>
                          <div className="text-[13px] font-bold text-slate-900 tabular-nums">
                            {v}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ─── Recent scenarios for this property ─── */}
      {propertyId && recent.length > 0 && (
        <section
          data-testid="wholesale-recent-scenarios"
          className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
        >
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
                Saved scenarios
              </div>
              <div className="text-[15px] font-bold text-slate-900 leading-tight">
                Recent runs · {propertyMap[propertyId]?.address?.street}
              </div>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {recent.slice(0, 6).map((s) => {
              const c = CLASS_META[s.outputs?.classification] || CLASS_META.review;
              return (
                <div key={s.id}
                     data-testid={`wholesale-scenario-${s.id}`}
                     className="flex items-center gap-3 px-5 py-3 hover:bg-amber-50/40 transition-colors">
                  <span className="text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ring-1"
                        style={{
                          backgroundColor: `${c.color}15`,
                          color: c.color,
                          borderColor: `${c.color}40`,
                        }}>
                    {c.label} · {s.outputs?.dealScore ?? "—"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-bold text-slate-900 truncate">
                      {s.scenario_name}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Recommended {fmtMoney(s.outputs?.recommendedOffer)} · Fee {fmtMoney(s.outputs?.actualAssignmentFee)} · {timeAgo(s.created_at)}
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                    s.status === "approved" ? "bg-emerald-100 text-emerald-700"
                    : s.status === "recommended" ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-600"
                  }`}>
                    {s.status}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

// ─────────────────────────────────────────── property picker ───────────

function PropertyPicker({ properties, value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = properties.find((p) => p.id === value);
  const filtered = useMemo(() => {
    if (!q.trim()) return properties;
    const t = q.trim().toLowerCase();
    return properties.filter((p) => {
      const s = `${p.address?.street || ""} ${p.address?.city || ""} ${p.address?.state || ""}`.toLowerCase();
      return s.includes(t);
    });
  }, [properties, q]);

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={loading || properties.length === 0}
        data-testid="wholesale-property-picker"
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white hover:border-amber-400 px-3 py-2 text-[13px] font-semibold text-slate-800 shadow-sm min-w-[240px] justify-between disabled:opacity-60"
      >
        <span className="truncate text-left">
          {loading ? "Loading…"
            : selected ? `${selected.address?.street || "Property"} · ${selected.address?.state || ""}`
            : "Select property"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div
          data-testid="wholesale-property-picker-menu"
          className="absolute right-0 top-full mt-1 w-[320px] max-h-[360px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl z-30 flex flex-col"
        >
          <div className="p-2 border-b border-slate-100">
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search address, city…"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-[12px] text-slate-500 text-center">
                No properties match.
              </div>
            ) : filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`wholesale-property-picker-opt-${p.id}`}
                onClick={() => { onChange(p.id); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2 text-[12.5px] hover:bg-amber-50 transition-colors border-b border-slate-50 ${
                  p.id === value ? "bg-amber-50/60" : ""
                }`}
              >
                <div className="font-semibold text-slate-900 truncate">
                  {p.address?.street || "—"}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {[p.address?.city, p.address?.state, p.address?.zip_code].filter(Boolean).join(", ")}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────── input widgets ────────────

function FormGroup({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

function MoneyInput({ label, value, onChange, testid, required }) {
  return (
    <FormGroup label={`${label}${required ? " *" : ""}`}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
        <input
          type="number" min="0" step="1"
          value={value} onChange={onChange}
          data-testid={testid}
          className="w-full rounded-lg border border-slate-200 bg-white pl-6 pr-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
        />
      </div>
    </FormGroup>
  );
}

function PctInput({ label, value, onChange, testid, required }) {
  return (
    <FormGroup label={`${label}${required ? " *" : ""}`}>
      <div className="relative">
        <input
          type="number" min="0" max="1" step="0.01"
          value={value} onChange={onChange}
          data-testid={testid}
          className="w-full rounded-lg border border-slate-200 bg-white pr-8 pl-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">×</span>
      </div>
    </FormGroup>
  );
}

// ─────────────────────────────────────────── KPI tile ────────────

function KpiTile({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="rounded-lg bg-slate-50/70 border border-slate-100 px-3 py-2"
         data-testid={testid}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-slate-500">
        <span className="h-1 w-1 rounded-full" style={{ backgroundColor: color }} />
        {label}
      </div>
      <div className="mt-0.5 text-[15px] font-bold text-slate-900 tabular-nums flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3 text-slate-400" />}
        <span>{value}</span>
      </div>
    </div>
  );
}

function WarningIcon({ severity }) {
  if (severity === "error") return <ShieldAlert className="w-3.5 h-3.5 text-rose-600 mt-0.5 shrink-0" />;
  if (severity === "warning") return <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />;
  return <Info className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />;
}
